import { Button } from '@invent/wl-ui-kit-next/ui';
import { ArrowRight } from 'lucide-react';
import * as React from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactFilters,
  CompactMenu,
  CompactTitle,
  DataTable,
  HelpPopover,
  NewTabLink,
  PageHeader,
  SelectChip,
  STICKY_TABLE_TOP,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type ICompactChip,
  type IDataColumn,
  type IDataGrouping
} from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type { IPage } from '../api/oversight-contracts';
import { oversightApi } from '../api/oversight-api';
import { LoadError, PastTheEnd, useAdminData } from '../components/admin-page';
import {
  CardLink,
  SearchResultsContext,
  type ICardLinkState
} from '../components/participant-link';
import { cycleCodesOf, SEARCH_RULE, useCatalogue } from '../components/record-filters';
import { SearchMatches } from '../components/search-matches';
import { StateCards } from '../components/state-cards';
import { FOUND_KIND_WORDS, findRecords, type IFoundRecord } from '../found-records';
import { responsePath, TEAM_PATHS } from '../paths';
import { countRecycled } from '../recycle-bin';
import {
  SCOPE_LABEL,
  SCOPE_NOUNS,
  SCOPE_PATH,
  searchedScopes,
  searchFailure,
  type SearchScope
} from '../record-search';
import { useOwnColumns } from './lists/work-page/columns';
import { WorkPageHeaderActions, useReadAt } from './lists/work-page/header-actions';
import {
  WORK_ITEMS_MAX_PAGE,
  WorkPager,
  dropPaging,
  readItemPages,
  useWorkPaging,
  type IWorkPaging
} from './lists/work-page/pager';
import { binColumns } from './recycle-bin-page';
import { RecordSearch } from './record-lists/record-work-page';
import { ParamChip } from './sec-adv/firm-filters';

/**
 * The search results page (SR-463): every record one search finds — People, Teams, Firms and the
 * SEC ADV register — on one page, where the top search's Enter leads. The client's ask: "searching
 * John and pressing Enter should list every John", whatever type the record is.
 *
 * **It reads the lists the top search reads, as their own pages read them** (`findRecords`, record
 * search D1): no search of its own, no ranking across lists — each list's order is survey-api's. A
 * row's name is its way to the record, ↗ to it in a new tab (`NewTabLink`).
 *
 * **A card is a view of this page** (`type=`), as on every work page: All results the table
 * grouped by list, each list's first `GROUP_ROWS` under a heading ("People · 10 of 23 · Show all");
 * a list's card that list's whole result here, paged (`WorkPager`); the Recycle bin's card the
 * bin's responses this search reaches (SR-490: a record held only in the bin is in no list). A
 * heading's *Show all* opens its list's card. Leaving is a press of its own — *Open in People* and
 * the like, the list's own page under the same search and cycle, with its filters, columns and
 * order.
 *
 * **Cycle is the one filter**, the one every list of records shares: it narrows the counts and the
 * rows, and goes with *Open in …*. The register has no cycles: under one it is not in All results,
 * and its card lists it whole. An account searching the register alone is offered no Cycle.
 *
 * **The search is the URL** — `q`, `cycle`, the card `type` and its window — and reads as it is
 * typed in the page's box (the lists' `RecordSearch`, a filter box: the app bar holds the search
 * itself). A change of either drops the window. A record opened here returns here
 * (`SearchResultsContext`, `results` in the link's state). A list the account does not open is
 * neither searched nor drawn (`searchedScopes`).
 */
export function AdminSearchResultsPage() {
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const scopes = searchedScopes(can);
  const q = (params.get('q') ?? '').trim();
  const cycle = params.get('cycle') ?? '';
  const typeParam = params.get('type');
  const binOpen = can('responses.browse');
  // The card open: a list's, the recycle bin's, or All results (null).
  const view: Nullable<View> =
    scopes.find((scope) => scope === typeParam) ??
    (binOpen && typeParam === 'recycle-bin' ? 'recycle-bin' : null);
  // Cycle narrows the lists of records; the register alone has none to narrow.
  const cycled = scopes.some((scope) => scope !== 'register');

  const overview = useOverview(scopes, q, cycle);
  const asked = overviewKey(q, cycle);
  // SR-490: how many of the bin's responses this search reaches — the Recycle bin card's count.
  const binned = useAdminData(
    async (signal) =>
      binOpen && q !== '' ? { asked, total: await countRecycled(q, cycle, signal) } : null,
    [asked, binOpen]
  );
  // A press on Refresh reads the open card's list again too.
  const [round, setRound] = React.useState(0);
  const [viewTotal, setViewTotal] = React.useState<Nullable<number>>(null);
  const readAt = useReadAt(q !== '' && overview.answered.asked === asked ? overview.answered : null);
  const toolbar = useWorkToolbar();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(new Set());
  const chosen = useOwnColumns<IFoundRecord>('search.results');
  const catalogue = useCatalogue();
  const cycles = React.useMemo(() => cycleCodesOf(catalogue, []), [catalogue]);
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);

  if (scopes.length === 0) return <Navigate to="/" replace />;

  /** A change of the search or the cycle starts at the top of the card. */
  function write(name: string, value: string, replace = false) {
    setParams(
      (current) => {
        const next = dropPaging(new URLSearchParams(current));
        if (value.trim() === '') next.delete(name);
        else next.set(name, value.trim());
        return next;
      },
      { replace }
    );
  }
  const search = (text: string) => write('q', text, true);
  const chooseCycle = (value: Nullable<string>) => write('cycle', value ?? '');

  const answers = overview.answered.asked === asked ? overview.answered.answers : {};
  // The register has no cycles: under one it is not in All results.
  const drawn = scopes.filter((scope) => !(cycle !== '' && scope === 'register'));
  const totalOf = (scope: SearchScope) => {
    const answer = answers[scope];
    return answer?.state === 'done' ? answer.page.total : null;
  };
  const done = drawn.flatMap((scope) => {
    const answer = answers[scope];
    return answer?.state === 'done' ? [answer.page] : [];
  });
  const grandTotal =
    q !== '' && done.length === drawn.length
      ? done.reduce((sum, page) => sum + page.total, 0)
      : null;
  const failures = drawn.flatMap((scope) => {
    const answer = answers[scope];
    return answer?.state === 'failed' ? [{ scope, message: answer.message }] : [];
  });
  const rows = done.flatMap((page) => page.items);
  const loading = q !== '' && drawn.some((scope) => answers[scope] === undefined);
  const binTotal = binned.data?.asked === asked ? binned.data.total : null;

  /** A list's own page under the search and the cycle — what *Open in …* opens. */
  const listPath = (target: View) => {
    const next = new URLSearchParams({ q });
    if (cycle !== '' && target !== 'register') next.set('cycle', cycle);
    return `${LIST_PATH[target]}?${next.toString()}`;
  };
  /** A card of this page: the search and the cycle kept, the window dropped. */
  const cardTo = (target: Nullable<View>) => {
    const next = new URLSearchParams();
    if (q !== '') next.set('q', q);
    if (cycle !== '') next.set('cycle', cycle);
    if (target !== null) next.set('type', target);
    return { search: next.toString() };
  };
  /** A card's count in words: "—" before a search, "!" for a failed read, "…" while out. */
  const counted = (total: Nullable<number>, failed = false) =>
    q === '' ? '—' : failed ? '!' : (total?.toLocaleString('en-US') ?? '…');

  const cardSpecs = [
    {
      key: 'all',
      to: cardTo(null),
      current: view === null,
      label: 'All results',
      count: counted(grandTotal),
      hint: 'The first of every list',
      data: { 'data-card': 'all' },
      countData: { 'data-count': grandTotal?.toString() ?? '' }
    },
    ...scopes.map((scope) => {
      const total = totalOf(scope);
      const failed = answers[scope]?.state === 'failed';
      return {
        key: scope,
        to: cardTo(scope),
        current: view === scope,
        label: SCOPE_LABEL[scope],
        count: counted(total, failed),
        hint: failed
          ? 'Could not be searched'
          : cycle !== '' && scope === 'register'
            ? 'Not by cycle'
            : `Every ${SCOPE_NOUNS[scope][0]} found`,
        data: { 'data-card': scope },
        countData: { 'data-count': total?.toString() ?? '' }
      };
    }),
    ...(binOpen
      ? [
          {
            key: 'recycle-bin',
            to: cardTo('recycle-bin'),
            current: view === 'recycle-bin',
            label: 'Recycle bin',
            count: counted(binTotal, binned.error !== null),
            hint: 'Responses moved out of play — not in All results',
            data: { 'data-card': 'recycle-bin' },
            countData: { 'data-count': binTotal?.toString() ?? '' }
          }
        ]
      : [])
  ];
  const cards = <StateCards label="Show" compact cards={cardSpecs} />;
  const openCard = cardSpecs.find((card) => card.current);

  const grouping: IDataGrouping<IFoundRecord> = {
    levels: [
      {
        id: 'scope',
        label: 'List',
        value: (row) => row.scope,
        compare: (left, right) =>
          scopes.indexOf(left as SearchScope) - scopes.indexOf(right as SearchScope),
        name: (value) => SCOPE_LABEL[value as SearchScope]
      }
    ],
    collapsed,
    onToggle: (key) =>
      setCollapsed((now) => {
        const next = new Set(now);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    count: (path, groupRows) => {
      const scope = path[0] as SearchScope;
      const total = totalOf(scope) ?? groupRows.length;
      return (
        <>
          {total > groupRows.length
            ? `· ${groupRows.length} of ${total.toLocaleString('en-US')} · `
            : `· ${total.toLocaleString('en-US')} · `}
          <Link
            to={cardTo(scope)}
            data-show-all={scope}
            className="font-medium text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            {total > groupRows.length
              ? `Show all ${SCOPE_NOUNS[scope][1]}`
              : `Only ${SCOPE_NOUNS[scope][1]}`}
          </Link>
        </>
      );
    }
  };

  const tableState: DataTableState | undefined =
    q === ''
      ? { kind: 'empty', content: 'Type a name, email, CRD, team or firm to search.' }
      : rows.length > 0
        ? undefined
        : loading
          ? { kind: 'loading' }
          : {
              kind: 'empty',
              content:
                failures.length > 0
                  ? `Nothing found for “${q}” in the lists that could be searched.`
                  : cycle !== ''
                    ? `Nothing matches “${q}” in cycle ${cycle}.`
                    : `Nothing matches “${q}”.`
            };

  // The columns marked with the words of the search the rows answer.
  const columnsFor = (marked: string) => chosen.resolve(resultColumns(marked));
  const columnsButton = chosen.button(resultColumns(q));

  const cycleFilter = cycled && (
    <SelectChip
      label="Cycle"
      any="Every cycle"
      value={cycle === '' ? null : cycle}
      options={cycles.map((code) => ({ value: code, label: code }))}
      onChoose={chooseCycle}
    />
  );
  const chipsWith = (open: (label: string) => true): ICompactChip[] =>
    cycle === ''
      ? []
      : [
          {
            key: 'cycle',
            chip: (
              <ParamChip
                words={`Cycle: ${cycle}`}
                body={null}
                onPress={() => open('Cycle')}
                onRemove={() => chooseCycle(null)}
              />
            )
          }
        ];
  const openFilters = () => {
    setFiltersOpen(true);
    return true as const;
  };
  const clearAll = () => chooseCycle(null);

  const refresh = {
    reading: overview.reading || binned.reading,
    readAt,
    onClick: () => {
      overview.reload();
      void binned.reload();
      setRound((now) => now + 1);
    }
  };
  const help = <AboutSearch scopes={scopes} />;
  const shownTotal = view === null ? grandTotal : viewTotal;
  const summary =
    q === ''
      ? null
      : shownTotal === null
        ? 'Searching…'
        : view === null
          ? resultsWords(shownTotal, q)
          : `${shownTotal.toLocaleString('en-US')} ${viewNoun(view, shownTotal)} for “${q}”`;
  const openIn = (compact: boolean) =>
    view !== null &&
    q !== '' && (
      <Button asChild variant="outline" size="sm" className={compact ? 'h-8 gap-1' : 'h-9 gap-1'}>
        <Link to={listPath(view)} data-open-list={view}>
          Open in {VIEW_LABEL[view]}
          <ArrowRight className="size-3.5" aria-hidden />
        </Link>
      </Button>
    );
  // The bin's card has its own columns, which the page's choice does not reach.
  const buttons = (compact: boolean) => (
    <>
      {openIn(compact)}
      {view !== 'recycle-bin' && columnsButton}
    </>
  );
  const searchBox = (compact: boolean) => (
    <RecordSearch
      q={q}
      label="Search results"
      placeholder="Name, email, CRD, team or firm…"
      onSearch={search}
      compact={compact}
    />
  );

  return (
    <>
      <PageHeader
        title="Search results"
        row
        actions={
          <WorkPageHeaderActions
            summary={summary}
            summaryData={{ 'data-results-summary': '' }}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled}
        label="Search results, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={
          cycle !== '' && <CompactChipLine chips={chipsWith(openFilters)} onClearAll={clearAll} />
        }
      >
        <CompactTitle>Search results</CompactTitle>
        {openCard && (
          <CompactMenu
            label="Show"
            chip
            trigger={
              <>
                {openCard.label}
                <span className="font-semibold tabular-nums">{openCard.count}</span>
              </>
            }
            items={cardSpecs.map((card) => ({
              key: card.key,
              label: card.label,
              to: card.to,
              current: card.current,
              count: card.count
            }))}
          />
        )}
        <CompactDivider />
        {searchBox(true)}
        {cycleFilter && (
          <CompactFilters
            count={cycle === '' ? 0 : 1}
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
          >
            {cycleFilter}
          </CompactFilters>
        )}
        <span className="flex-1" />
        <CompactDivider />
        {buttons(true)}
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        {cards}
        <WorkToolbar
          toolbar={toolbar}
          status={{
            reading: overview.reading,
            count: shownTotal === null ? undefined : `${shownTotal.toLocaleString('en-US')} found`
          }}
          search={searchBox(false)}
          quick={cycleFilter || null}
          quickCount={cycle === '' ? 0 : 1}
          end={buttons(false)}
        />
        {cycle !== '' && (
          <CompactChipLine
            label="Filters"
            chips={chipsWith(toolbar.openQuick)}
            onClearAll={clearAll}
          />
        )}
        <div ref={setControlsEnd} aria-hidden data-controls-end="" className="-mt-3 h-0" />

        {view === null ? (
          <>
            {failures.map((failure) => (
              <LoadError key={failure.scope} message={failure.message} />
            ))}
            <FoundTable
              label="Results"
              columns={columnsFor(q)}
              rows={tableState ? null : rows}
              state={tableState}
              busy={overview.reading && rows.length > 0}
              grouping={grouping}
            />
          </>
        ) : q === '' ? null : view === 'recycle-bin' ? (
          <BinResults key={view} q={q} cycle={cycle} round={round} onTotal={setViewTotal} />
        ) : (
          <ScopeResults
            key={view}
            scope={view}
            q={q}
            cycle={cycle}
            round={round}
            columnsFor={columnsFor}
            onTotal={setViewTotal}
          />
        )}
      </div>
    </>
  );
}

/** The rows All results shows of each list; the rest are on the list's card. */
const GROUP_ROWS = 10;

/** A card of the page: a list's, or the recycle bin's (SR-490). */
type View = SearchScope | 'recycle-bin';

/** Each card's own page, which *Open in …* opens. */
const LIST_PATH: Readonly<Record<View, string>> = {
  ...SCOPE_PATH,
  'recycle-bin': TEAM_PATHS.recycleBin
};

const VIEW_LABEL: Readonly<Record<View, string>> = { ...SCOPE_LABEL, 'recycle-bin': 'Recycle bin' };

/** What a card's rows are, one or many. */
function viewNoun(view: View, total: number): string {
  if (view === 'recycle-bin') {
    return total === 1 ? 'response in the recycle bin' : 'responses in the recycle bin';
  }
  return SCOPE_NOUNS[view][total === 1 ? 0 : 1];
}

// ---------------------------------------------------------------------------
// A card's whole result
// ---------------------------------------------------------------------------

/** The history state a record opened here leaves: its way back is this page. */
function resultsState(search: string): ICardLinkState {
  return { search, results: true };
}

/** Found records in the page's columns, each opening its record with the way back here. */
function FoundTable({
  label,
  columns,
  rows,
  state,
  busy,
  grouping
}: {
  label: string;
  columns: IDataColumn<IFoundRecord>[];
  rows: Nullable<IFoundRecord[]>;
  state: DataTableState | undefined;
  busy: boolean;
  grouping?: IDataGrouping<IFoundRecord>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <SearchResultsContext.Provider value={location.search}>
      <DataTable
        label={label}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.key}
        rowProps={(row) =>
          ({
            'data-kind': row.kind,
            'data-scope': row.scope
          }) as React.HTMLAttributes<HTMLTableRowElement>
        }
        onOpen={(row) => navigate(row.path, { state: resultsState(location.search) })}
        grouping={grouping}
        busy={busy}
        state={state}
        stickyTop={STICKY_TABLE_TOP}
        minWidth="56rem"
      />
    </SearchResultsContext.Provider>
  );
}

/**
 * A card's whole result, paged: the read under `query` (the search, the cycle, the card) and the
 * window, each answer kept with the query it answers. The last answer stays, dimmed, while the
 * next is out — but its total is reported, and its rows' words marked, only under the query it
 * answers, never another search's.
 */
function useCardPage<T>(
  query: unknown[],
  read: (page: number, pageSize: number, signal: AbortSignal) => Promise<IPage<T>>,
  onTotal: (total: Nullable<number>) => void
): {
  paging: IWorkPaging;
  shown: Nullable<{ key: string; page: IPage<T> }>;
  current: boolean;
  error: Nullable<string>;
  failed: boolean;
  reading: boolean;
} {
  const paging = useWorkPaging();
  const { page, perPage, pages } = paging;
  const key = JSON.stringify(query);
  const asked = JSON.stringify([key, page, perPage, pages]);
  const { data, error, reading } = useAdminData(
    async (signal) => ({
      key,
      asked,
      page: await readItemPages(
        (at, pageSize) => read(at, pageSize, signal),
        page,
        perPage,
        pages,
        WORK_ITEMS_MAX_PAGE
      )
    }),
    [asked]
  );
  const failed = error !== null && !reading;
  const shown = failed && data?.asked !== asked ? null : data;
  const current = shown?.key === key;
  const total = current ? (shown?.page.total ?? null) : null;
  React.useEffect(() => onTotal(total), [total]);
  React.useEffect(() => () => onTotal(null), []);
  return { paging, shown, current, error, failed, reading };
}

/** One list's whole result under the search and the cycle, paged — its card on this page. */
function ScopeResults({
  scope,
  q,
  cycle,
  round,
  columnsFor,
  onTotal
}: {
  scope: SearchScope;
  q: string;
  cycle: string;
  round: number;
  columnsFor: (marked: string) => IDataColumn<IFoundRecord>[];
  onTotal: (total: Nullable<number>) => void;
}) {
  // The register has no cycles: it is read under the search alone.
  const byCycle = scope === 'register' ? undefined : cycle || undefined;
  const { paging, shown, current, error, failed, reading } = useCardPage(
    [scope, q, byCycle, round],
    (page, pageSize, signal) => findRecords(scope, { q, page, pageSize, cycle: byCycle }, signal),
    onTotal
  );
  const one = SCOPE_NOUNS[scope][0];
  const state = pageState(
    shown?.page ?? null,
    failed ? error : null,
    SCOPE_NOUNS[scope],
    paging.setPage,
    () => `No ${one} matches “${q}”${byCycle ? ` in cycle ${byCycle}` : ''}.`
  );
  // The rows' words are the search they answer: the last one's while the next is out.
  const marked = current ? q : (JSON.parse(shown?.key ?? '[]')[1] ?? q);
  return (
    <>
      {failed && shown !== null && (
        <LoadError message={`Could not refresh — showing the list as last read. ${error}`} />
      )}
      <FoundTable
        label={SCOPE_LABEL[scope]}
        columns={columnsFor(marked)}
        rows={state ? null : (shown?.page.items ?? null)}
        state={state}
        busy={reading && shown !== null}
      />
      {shown && <WorkPager {...paging.pager} total={shown.page.total} busy={reading} sticky />}
    </>
  );
}

/** The recycle bin's responses this search reaches (SR-490), paged, in the bin's own columns — a
 *  record held only in the bin is in no list, so this card is where the search finds it. */
function BinResults({
  q,
  cycle,
  round,
  onTotal
}: {
  q: string;
  cycle: string;
  round: number;
  onTotal: (total: Nullable<number>) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { paging, shown, error, failed, reading } = useCardPage(
    [q, cycle, round],
    (page, pageSize, signal) =>
      oversightApi.listResponses(
        { q, cycleCode: cycle || undefined, status: ['recycled'], page, pageSize },
        signal
      ),
    onTotal
  );
  const state = pageState(
    shown?.page ?? null,
    failed ? error : null,
    ['response', 'responses'],
    paging.setPage,
    () =>
      `No response in the recycle bin is of a record matching “${q}”` +
      `${cycle ? ` in cycle ${cycle}` : ''}.`
  );
  return (
    <>
      {failed && shown !== null && (
        <LoadError message={`Could not refresh — showing the list as last read. ${error}`} />
      )}
      <SearchResultsContext.Provider value={location.search}>
        <DataTable
          label="Recycle bin"
          columns={binColumns({ survey: 'Survey', cycle: 'Cycle' }, true)}
          rows={state ? null : (shown?.page.items ?? null)}
          rowKey={(row) => row.responseId}
          onOpen={(row) =>
            navigate(responsePath(row.responseId), { state: resultsState(location.search) })
          }
          busy={reading && shown !== null}
          state={state}
          stickyTop={STICKY_TABLE_TOP}
        />
      </SearchResultsContext.Provider>
      {shown && <WorkPager {...paging.pager} total={shown.page.total} busy={reading} sticky />}
    </>
  );
}

/** A card's table state: loading, failed, past the end, empty in the card's words — or rows. */
function pageState(
  page: Nullable<IPage<unknown>>,
  error: Nullable<string>,
  counted: [string, string],
  onPage: (page: number) => void,
  empty: () => string
): DataTableState | undefined {
  if (page === null) {
    return error ? { kind: 'error', content: <LoadError message={error} /> } : { kind: 'loading' };
  }
  if (page.items.length > 0) return undefined;
  return {
    kind: 'empty',
    content:
      page.total > 0 ? (
        <PastTheEnd
          page={page.page}
          pageSize={page.pageSize}
          total={page.total}
          counted={counted}
          onPage={onPage}
        />
      ) : (
        empty()
      )
  };
}

// ---------------------------------------------------------------------------
// The counts and All results' rows
// ---------------------------------------------------------------------------

type Found =
  | { state: 'done'; page: IPage<IFoundRecord> }
  | { state: 'failed'; message: string };

interface IOverview {
  /** The search and cycle the answers are for (`overviewKey`). */
  asked: string;
  answers: Partial<Record<SearchScope, Found>>;
}

function overviewKey(q: string, cycle: string): string {
  return JSON.stringify([q, cycle]);
}

/** Each list's first rows and total under the search and the cycle, each landing as it comes,
 *  drawn only under the query it answers. */
function useOverview(scopes: readonly SearchScope[], q: string, cycle: string) {
  const scopeKey = scopes.join(' ');
  const [answered, setAnswered] = React.useState<IOverview>({ asked: '', answers: {} });
  const [reading, setReading] = React.useState(false);
  const [round, setRound] = React.useState(0);
  React.useEffect(() => {
    if (q === '' || scopeKey === '') {
      setAnswered({ asked: '', answers: {} });
      // The reads a cleared box cancelled say nothing more, so nothing is out.
      setReading(false);
      return undefined;
    }
    const asked = overviewKey(q, cycle);
    const controller = new AbortController();
    // A refresh keeps the rows on screen while the next answers come.
    setAnswered((now) => (now.asked === asked ? now : { asked, answers: {} }));
    const land = (scope: SearchScope, answer: Found) => {
      if (controller.signal.aborted) return;
      setAnswered((now) =>
        now.asked === asked ? { asked, answers: { ...now.answers, [scope]: answer } } : now
      );
    };
    const lists = scopeKey.split(' ') as SearchScope[];
    let out = lists.length;
    setReading(true);
    for (const scope of lists) {
      findRecords(scope, { q, pageSize: GROUP_ROWS, cycle: cycle || undefined }, controller.signal)
        .then(
          (page) => land(scope, { state: 'done', page }),
          (error: unknown) =>
            land(scope, { state: 'failed', message: searchFailure(error, SCOPE_LABEL[scope]) })
        )
        .finally(() => {
          out -= 1;
          if (out === 0 && !controller.signal.aborted) setReading(false);
        });
    }
    return () => controller.abort();
  }, [q, cycle, scopeKey, round]);
  return { answered, reading, reload: () => setRound((now) => now + 1) };
}

// ---------------------------------------------------------------------------
// The columns
// ---------------------------------------------------------------------------

function resultColumns(q: string): IDataColumn<IFoundRecord>[] {
  return [
    {
      id: 'name',
      header: 'Name',
      cell: (row) => {
        const Icon = row.icon;
        return (
          <>
            {/* The arrow at the cell's right edge, as on the lists. */}
            <span className="flex items-start justify-between gap-1">
              <span className="inline-flex min-w-0 items-center gap-2">
                <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <CardLink to={row.path} fromList className="min-w-0 break-words">
                  {row.name}
                </CardLink>
              </span>
              <NewTabLink
                to={row.path}
                label={`Open ${row.name} in a new tab`}
                className="mt-0.5 shrink-0"
              />
            </span>
            {row.place && <p className="mt-0.5 text-xs text-muted-foreground">{row.place}</p>}
            <SearchMatches matches={row.matches} q={q} />
          </>
        );
      }
    },
    {
      id: 'type',
      header: 'Type',
      className: 'whitespace-nowrap',
      cell: (row) => FOUND_KIND_WORDS[row.kind]
    },
    {
      id: 'contact',
      header: 'Email, lead or number',
      cell: (row) =>
        row.contact ? (
          <span className="break-all">{row.contact}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
    },
    {
      id: 'surveys',
      header: 'Surveys',
      className: 'whitespace-nowrap',
      cell: (row) =>
        row.surveys.length > 0 ? (
          row.surveys.join(', ')
        ) : (
          <span className="text-muted-foreground">—</span>
        )
    },
    {
      id: 'account',
      header: 'Account',
      optional: true,
      hidden: true,
      cell: (row) =>
        row.account === null ? (
          <span className="text-muted-foreground">{row.kind === 'register' ? '—' : 'No account'}</span>
        ) : (
          <>
            <span className="break-all">{row.account.email}</span>
            <p className="mt-0.5 text-xs text-muted-foreground">{row.account.status}</p>
          </>
        )
    },
    {
      id: 'created',
      header: 'Created',
      optional: true,
      hidden: true,
      className: 'text-xs whitespace-nowrap text-muted-foreground',
      cell: (row) => (row.createdAt === null ? '—' : formatDateTime(row.createdAt))
    }
  ];
}

function resultsWords(total: number, q: string): string {
  return `${total.toLocaleString('en-US')} ${total === 1 ? 'result' : 'results'} for “${q}”`;
}

/** The "?": what the page holds and how it reads. */
function AboutSearch({ scopes }: { scopes: readonly SearchScope[] }) {
  return (
    <HelpPopover label="About search results" align="end">
      <p>
        Every record the search finds, whatever its type:{' '}
        {scopes.map((scope) => SCOPE_LABEL[scope]).join(', ')}. Each list is searched as its own page
        searches it, best match first, and shows its first {GROUP_ROWS} here.
      </p>
      <ul className="grid list-disc gap-1 pl-5">
        <li>
          <span className="font-medium">A card, or Show all</span> — that list&apos;s whole result,
          here. <span className="font-medium">Open in …</span> opens the list&apos;s own page under
          the same search, with its filters, columns and order.
        </li>
        <li>
          <span className="font-medium">Cycle</span> narrows every list and the counts, and goes with
          you to a list. The SEC ADV register has no cycles: under one it is not in All results, and
          its card lists it whole.
        </li>
        <li>
          <span className="font-medium">People</span> includes people named on a team&apos;s or
          firm&apos;s roster who have not registered.
        </li>
        <li>
          <span className="font-medium">Recycle bin</span> — a record whose every response is in
          the recycle bin is in no list; its card shows the bin&apos;s responses this search
          reaches.
        </li>
      </ul>
      <p>The search reads as you type. {SEARCH_RULE} Each row says why it matched.</p>
    </HelpPopover>
  );
}
